import { useEffect } from 'react';
import { RouterProvider } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { configureApiClient, forceRefresh } from '@/shared/api/client';
import { useAuthStore } from '@/shared/lib/auth/store';
import { i18n } from '@/shared/lib/i18n';
import { ThemeProvider } from '@/shared/ui/chakra/provider';
import { Toaster, toaster } from '@/shared/ui/chakra/toaster';
import { fetchMe } from '@/features/auth/api/me';
import { UpdateToast } from '@/features/pwa-update/UpdateToast';
import { bootstrapSession } from './bootstrapSession';
import { router } from './router';

configureApiClient({
  getAccessToken: () => useAuthStore.getState().accessToken,
  setAccessToken: (token) => useAuthStore.getState().setAccessToken(token),
  // A background 401 whose refresh failed (expired/revoked session) — unlike the explicit
  // sign-out handlers in AppShell/ClientShell, nothing else here ever redirects: route guards
  // only run on navigation, so without this the shell/sidebar/current page would stay mounted
  // showing stale data against an anonymous session until a manual reload. Skip the redirect (and
  // the toast) if we're already on /login — an explicit sign-out already reset()'d + navigated
  // itself, and this would otherwise fire again as a harmless but noisy duplicate.
  onAuthInvalidated: () => {
    useAuthStore.getState().reset();
    if (router.state.location.pathname === '/login') return;
    toaster.create({ title: i18n.t('auth.session_expired'), type: 'info' });
    void router.navigate({ to: '/login' });
  },
});

export function App() {
  const { t } = useTranslation();
  const status = useAuthStore((s) => s.status);
  const setAccessToken = useAuthStore((s) => s.setAccessToken);
  const setUser = useAuthStore((s) => s.setUser);
  const setStatus = useAuthStore((s) => s.setStatus);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Coalesced single-flight (shared with the api client) so React
      // StrictMode's double-invoke — or a concurrent 401 refresh — can't
      // fire two rotating refreshes and trip the reuse-detection family
      // invalidation that logs the user out (bug #18 / RFC 6819 §5.2.2.3).
      // Экран входа — только на прямой ответ «сессии нет»; сбой сервера пережидается повторами
      // (см. `bootstrapSession`).
      const result = await bootstrapSession({
        refresh: forceRefresh,
        fetchMe,
        sleep: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
        isCancelled: () => cancelled,
      });
      if (cancelled || !result) return;
      if (result.status === 'authenticated') {
        setUser(result.me);
        setStatus('authenticated');
        return;
      }
      setAccessToken(null);
      setStatus('anonymous');
    })();
    return () => {
      cancelled = true;
    };
  }, [setAccessToken, setUser, setStatus]);

  if (status === 'loading') {
    return (
      <main
        style={{
          minHeight: '100vh',
          display: 'grid',
          placeItems: 'center',
          background: 'var(--color-bg)',
          color: 'var(--color-text-second)',
        }}
      >
        {t('common.loading')}
      </main>
    );
  }

  return (
    <ThemeProvider>
      <RouterProvider router={router} />
      <Toaster />
      <UpdateToast />
    </ThemeProvider>
  );
}
